import { CHARACTERISTICS_MAP } from '../constants/characteristics';
import { PRICE_CHANNELS_MAP } from '../constants/product_prices';

type ProductCharacteristicsResponse =
  | Record<string, { characteristics: any[] }>
  | null
  | undefined;

export function getDetalhesCharacteristicStringValue(
  characteristics: ProductCharacteristicsResponse,
  characteristicId: number,
): string | null {
  const raw = characteristics?.Detalhes?.characteristics?.find(
    (c: { id: number; value?: unknown }) => c.id === characteristicId,
  )?.value;
  if (raw == null) return null;
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function findPropertyByName(
  characteristics: any[] = [],
  name: string,
): string {
  return characteristics.find((char) => char.name === name)?.value || '';
}

/**
 * Flattens `GET /products/:id/characteristics` (grouped by "Detalhes", etc.)
 * into the array shape used by cart lines and Typesense product cards.
 */
export function flattenProductCharacteristicsFromApiResponse(
  characteristicsResponse: ProductCharacteristicsResponse,
): {
  id: number;
  name: string;
  value: string;
  valueJson: Record<string, unknown> | null;
}[] {
  if (!characteristicsResponse || typeof characteristicsResponse !== 'object') {
    return [];
  }

  const flat: {
    id: number;
    name: string;
    value: string;
    valueJson: Record<string, unknown> | null;
  }[] = [];

  for (const group of Object.values(characteristicsResponse)) {
    if (!group?.characteristics || !Array.isArray(group.characteristics)) {
      continue;
    }
    for (const char of group.characteristics) {
      if (char?.id == null) continue;
      flat.push({
        id: Number(char.id),
        name: String(char.name ?? ''),
        value: char.value != null ? String(char.value) : '',
        valueJson:
          char.valueJson != null && typeof char.valueJson === 'object'
            ? (char.valueJson as Record<string, unknown>)
            : null,
      });
    }
  }

  return flat;
}

export function getProductMaxInstallmentsFromCharacteristicSources(
  ...sources: Array<any[] | undefined | null>
): number | undefined {
  for (const characteristics of sources) {
    const maxInstallments = getProductMaxInstallmentsFromCharacteristics(
      characteristics ?? [],
    );
    if (maxInstallments !== undefined) {
      return maxInstallments;
    }
  }

  return undefined;
}

export function getProductMaxInstallmentsFromCharacteristics(
  characteristics: any[] = [],
): number | undefined {
  const installmentsChar = characteristics.find(
    (char) => char.id === CHARACTERISTICS_MAP.MAX_CARD_INSTALLMENTS.id,
  );
  if (!installmentsChar?.value) return undefined;

  const maxInstallments = Number(installmentsChar.value);
  if (!Number.isFinite(maxInstallments) || maxInstallments < 1) {
    return undefined;
  }

  return Math.floor(maxInstallments);
}

export function getOrderLimitFromCharacteristics(
  characteristics: any[] = [],
): number | undefined {
  const orderLimitChar = characteristics.find(
    (char) => char.id === CHARACTERISTICS_MAP.ORDER_LIMIT.id,
  );
  if (!orderLimitChar?.value) return undefined;

  const limit = Number(orderLimitChar.value);
  return Number.isNaN(limit) ? undefined : limit;
}

export function hasPBMCharacteristic(characteristics: any[] = []): boolean {
  return (
    characteristics?.some(
      (char: any) =>
        char?.id === CHARACTERISTICS_MAP.PBM_PROGRAM.id &&
        char?.value === 'true',
    ) ?? false
  );
}

export function findPriceChannelEntry(
  productPrices: any[] = [],
  channelId: number,
) {
  return productPrices?.find(
    (p: any) => Number(p?.price_channel_id) === Number(channelId),
  );
}

export function hasValidPBMPriceChannel(productPrices: any[] = []): boolean {
  const entry = findPriceChannelEntry(
    productPrices,
    PRICE_CHANNELS_MAP.PBM_PROGRAM.id,
  );
  return Boolean(
    entry && Number(entry?.price) > 0 && Number(entry?.sale_price) > 0,
  );
}

export function getPBMSalePrice(productPrices: any[] = []): number | undefined {
  const entry = findPriceChannelEntry(
    productPrices,
    PRICE_CHANNELS_MAP.PBM_PROGRAM.id,
  );
  const value = Number(entry?.sale_price);
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

/** Default catalog price channel (aligns with API OrderValidator channel 1). */
const DEFAULT_PRICE_CHANNEL_ID = 1;

/**
 * True when a product has a usable (> 0) selling price.
 *
 * Prevents zero-priced products (e.g. missing/incomplete catalog pricing) from
 * reaching the cart, which breaks the checkout shipping quotation.
 * Checks the top-level `sale_price`/`price` first, then the default price channel.
 */
export function hasSellablePrice(product?: {
  sale_price?: number | null;
  price?: number | null;
  product_prices?: any[];
} | null): boolean {
  if (!product) return false;

  const salePrice = Number(product.sale_price ?? 0);
  const price = Number(product.price ?? 0);
  if (salePrice > 0 || price > 0) return true;

  const defaultChannel = findPriceChannelEntry(
    product.product_prices ?? [],
    DEFAULT_PRICE_CHANNEL_ID,
  );
  const channelPrice = Number(
    defaultChannel?.sale_price ?? defaultChannel?.price ?? 0,
  );
  return channelPrice > 0;
}

/** True when characteristic 35 (Tem Genérico) is enabled on the product. */
export function productHasGenericOption(
  characteristics: { id: number; value?: unknown }[] | undefined,
): boolean {
  const hasGenericChar = characteristics?.find(
    (item) => item?.id === CHARACTERISTICS_MAP.HAS_GENERIC.id,
  );
  if (!hasGenericChar) return false;
  const value = hasGenericChar.value;
  if (typeof value === 'string') {
    return value.toLowerCase() === 'true';
  }
  return Boolean(value);
}

/**
 * Resolves listing/suggestion price: laboratory (channel 15) when lower than store sale_price.
 */
export function getListingDisplayPrice(item: {
  sale_price?: number | null;
  price?: number | null;
  product_prices?: { price_channel_id?: number; sale_price?: number }[];
}): {
  displayAmount: number | null;
  compareAmount: number | null;
  isLaboratoryPrice: boolean;
} {
  const salePrice =
    item?.sale_price != null ? Number(item.sale_price) : null;
  const originalPrice = item?.price != null ? Number(item.price) : null;
  const pbmPrice = getPBMSalePrice(item?.product_prices ?? []);

  if (
    pbmPrice != null &&
    salePrice != null &&
    salePrice > 0 &&
    pbmPrice < salePrice
  ) {
    return {
      displayAmount: pbmPrice,
      compareAmount: salePrice,
      isLaboratoryPrice: true,
    };
  }

  const hasStoreDiscount =
    originalPrice != null &&
    salePrice != null &&
    originalPrice > salePrice &&
    originalPrice > 0;

  return {
    displayAmount: salePrice,
    compareAmount: hasStoreDiscount ? originalPrice : null,
    isLaboratoryPrice: false,
  };
}

export function shouldShowPBM({
  characteristics,
  productPrices,
}: {
  characteristics: any[] | undefined;
  productPrices: any[] | undefined;
}): boolean {
  return Boolean(
    hasPBMCharacteristic(characteristics || []) &&
      hasValidPBMPriceChannel(productPrices || []),
  );
}

export function getRecipeValidityDaysFromCharacteristics(
  characteristics: any[] = [],
): number | undefined {
  const recipeValidityChar = characteristics.find(
    (char) => char.id === CHARACTERISTICS_MAP.RECIPE_VALIDITY_DAYS.id,
  );
  if (!recipeValidityChar?.value) return undefined;

  const days = Number(recipeValidityChar.value);
  return Number.isNaN(days) ? undefined : days;
}

/**
 * Extracts characteristic 33 (Recipe Validity Days) from the nested API response structure.
 * The API returns characteristics grouped by characteristic groups.
 * @param characteristicsResponse - The response from /products/{id}/characteristics endpoint
 * @returns The value of characteristic 33 as a number, or undefined if not found
 */
export function extractRecipeValidityDaysFromResponse(
  characteristicsResponse: Record<string, { characteristics: any[] }> | null | undefined,
): number | undefined {
  if (!characteristicsResponse) return undefined;

  // Iterate through all characteristic groups (e.g., "Descrição", "Detalhes")
  for (const groupKey in characteristicsResponse) {
    const group = characteristicsResponse[groupKey];
    if (group?.characteristics && Array.isArray(group.characteristics)) {
      // Find characteristic 33 in this group
      const recipeValidityChar = group.characteristics.find(
        (char: any) => char.id === CHARACTERISTICS_MAP.RECIPE_VALIDITY_DAYS.id,
      );
      
      if (recipeValidityChar?.value) {
        const days = Number(recipeValidityChar.value);
        if (!Number.isNaN(days) && days > 0) {
          return days;
        }
      }
    }
  }

  return undefined;
}

export type PromotionalPackConfig = Record<string, unknown> & { buy: number };

export type PromotionalPack = {
  config: PromotionalPackConfig;
  /** Text from `value` when it is a human label (e.g. "Leve 3 pague 2"), not JSON. */
  label: string | null;
};

function parseJsonObject(raw: unknown): Record<string, unknown> | null {
  if (raw != null && typeof raw === 'object') {
    return Array.isArray(raw) ? null : (raw as Record<string, unknown>);
  }
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{')) return null;
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/**
 * Reads the "Leve mais e pague menos" characteristic. The config must be a JSON
 * object with a positive integer `buy`, in `valueJson` or as a JSON string in
 * `value`. Plain-text values like "Leve 3 pague 2" are not a valid config.
 */
export function getPromotionalPack(
  characteristics: unknown,
): PromotionalPack | null {
  if (!Array.isArray(characteristics)) return null;
  const characteristic = characteristics.find(
    (item) => item?.id === CHARACTERISTICS_MAP.PROMOTIONAL_PACK.id,
  );
  if (!characteristic) return null;

  const config =
    parseJsonObject(characteristic.valueJson) ??
    parseJsonObject(characteristic.value);
  const buy = Number(config?.buy);
  if (!config || !Number.isInteger(buy) || buy <= 0) return null;

  const rawValue =
    typeof characteristic.value === 'string' ? characteristic.value.trim() : '';
  const label = rawValue && !parseJsonObject(rawValue) ? rawValue : null;

  return { config: { ...config, buy }, label };
}

export function promotionalPackHasPayRule(characteristics: unknown): boolean {
  const pack = getPromotionalPack(characteristics);
  return !!pack && Object.prototype.hasOwnProperty.call(pack.config, 'pay');
}

/**
 * Checks if a product has enough stock quantity for the promotional pack to apply.
 * @param product - The product to check
 * @returns true if the product has enough stock for the promotional pack, false otherwise
 */
export function hasEnoughStockForPromotionalPack(product: {
  quantity?: number | null;
  characteristics?: any[];
}): boolean {
  const promotional_pack_quantity = getPromotionalPack(product.characteristics)
    ?.config.buy;

  if (!promotional_pack_quantity) return false;

  const productQuantity = Number(product.quantity) || 0;
  
  return productQuantity >= promotional_pack_quantity;
}